import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { VariableSizeList } from 'react-window';
import { DragTypes, MoveStates } from './constants';
import * as styles from './ContentMapColumn.module.scss';
import ContentMapColumnBreadcrumbs from './ContentMapColumnBreadcrumbs';
import topicPolicyCheck, { TopicPolicyAction } from './topicPolicyCheck';
import TopicTreeCard, { getCardHeight } from './treeCard/TopicTreeCard';
import type { TopicTreeCardCallbacks } from './treeCard/TopicTreeCard';
import type {
  SelectedPosts,
  TopicForReorganization,
  TopicReorganizationMetadata,
  TopicReorganizationTree,
} from './useTopicReorganization';
import type { ContentMapGetDropParams } from './utils/types';
import WindowItem from './WindowItem';
import type { RootState } from '../app/redux/getReduxStore';
import useAppSelector from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated';
import type { DocumentType } from '../graphql/generated';
import useStayInViewport from './utils/useStayInViewport';
import { UserType } from '../graphql/generated/graphql';

const POST_FRAGMENT = gql(`
  fragment ContentMapColumn_postsMap on Post {
    id
    ...TopicTreeCard_postsMap
  }
`);

const getTopicIdFromTreeChildren = (tree: TopicReorganizationTree): string[] =>
  tree.children.flatMap((child) => [
    child.topic.id,
    ...getTopicIdFromTreeChildren(child),
  ]);

const IS_TOPIC_NODE_REORGANIZABLE_TOPIC_FRAGMENT = gql(`
  fragment isTopicNodeReorganizable_topic on Topic {
    id
    ...topicPolicyCheck_topic
  }
`);

interface IsTopicNodeReorganizableArgs {
  topic: DocumentType<typeof IS_TOPIC_NODE_REORGANIZABLE_TOPIC_FRAGMENT>;
  reorganizationMetadata: TopicReorganizationMetadata;
}

const isTopicNodeReorganizable = (
  { topic, reorganizationMetadata }: IsTopicNodeReorganizableArgs,
  user: RootState['session']['user'],
) =>
  user.type === UserType.Admin ||
  reorganizationMetadata.isNew ||
  topicPolicyCheck(TopicPolicyAction.ReorganizeTopic, topic, user);

const COLUMN_WIDTH = parseInt(styles.columnWidth, 10);
export const SPECIAL_COLUMN_PADDING = parseInt(styles.specialColumnPadding, 10);

interface ContentMapColumnProps
  extends Omit<TopicTreeCardCallbacks, 'onExpandedChange'> {
  tree: TopicReorganizationTree;
  specialColumn: 'posts' | 'unorganized' | null;
  showPosts: boolean;
  selectedPosts: SelectedPosts;
  allExpanded: boolean;
  isReorganizing: boolean;
  getDropParams: ContentMapGetDropParams;
  loadPostEdges: (topicId: string) => void;
  postsMap: Record<string, DocumentType<typeof POST_FRAGMENT>>;
  loadPost: (postId: string) => void;
}

const ContentMapColumn = ({
  tree,
  specialColumn,
  isReorganizing,
  showPosts,
  selectedPosts,
  getDropParams,
  allExpanded,
  onPostSelectedChange,
  onPostDeletedChange,
  onPostPinnedChange,
  onPostArchivedChange,
  onTopicSelectedChange,
  onTopicDeletedChange,
  onTopicNameChange,
  onSubtopicCreate,
  loadPostEdges,
  postsMap,
  loadPost,
}: ContentMapColumnProps) => {
  const [height, setHeight] = useState<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([tree.topic]);
  const [expandedByTopicId, setExpandedByTopicId] = useState<
    Record<string, boolean>
  >({});
  const [dragOverData, setDragOverData] = useState<
    { topicId: string; key: string } | { topicId?: never; key?: never }
  >({});
  const isDragOver = dragOverData.topicId === tree.topic.id;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<VariableSizeList | null>(null);
  const listInnerRef = useRef<HTMLElement | null>(null);

  const getPropsForStayInViewport = useStayInViewport((ids) => {
    ids.forEach((id) => loadPostEdges(id));
  });

  const currentUser = useAppSelector((state) => state.session.user);
  const isRootTopicDraggable = isTopicNodeReorganizable(tree, currentUser);

  const dragSpec = useMemo(
    () => ({
      type: DragTypes.column,
      item: {
        type: DragTypes.column,
        toMove: {
          postKeys: [],
          pinnedPostKeys: [],
          topicIds: [tree.topic.id],
        },
      },
      canDrag: isReorganizing && isRootTopicDraggable && !specialColumn,
      collect: () => null,
    }),
    [isReorganizing, isRootTopicDraggable, tree.topic, specialColumn],
  );
  const [, dragRef, preview] = useDrag(dragSpec);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const isExpanded = useCallback(
    (topicId: string) => {
      if (Object.prototype.hasOwnProperty.call(expandedByTopicId, topicId)) {
        return expandedByTopicId[topicId] as boolean;
      }
      return allExpanded;
    },
    [expandedByTopicId, allExpanded],
  );

  useEffect(() => {
    setExpandedByTopicId({});
  }, [allExpanded]);

  const handleDragOverChange = useCallback(
    (over: boolean, data: { topicId: string; key: string }) =>
      setDragOverData((prev) => {
        if (over) {
          return data;
        }
        if (!over && prev.key === data.key) {
          return {};
        }
        return prev;
      }),
    [],
  );

  const rows = useMemo(() => {
    const commonProps = {
      showPosts,
      getDropParams,
      isReorganizing,
      columnSelected: tree.selected,
      dragOverTopicId: dragOverData.topicId,
      onPostSelectedChange,
      onPostDeletedChange,
      onPostPinnedChange,
      onPostArchivedChange,
      onTopicSelectedChange,
      onTopicDeletedChange,
      onTopicNameChange,
      onSubtopicCreate,
      onDragOverChange: handleDragOverChange,
    };

    const result: {
      topic: TopicForReorganization;
      key: string;
      render: (style: CSSProperties) => ReactNode;
      height: number;
    }[] = [];

    const addTreeToResult = (
      t: TopicReorganizationTree,
      ancestorsReorganizationMetadata: TopicReorganizationMetadata[] = [],
    ) => {
      const { topic, reorganizationMetadata } = t;
      const isHeader = t === tree;
      const shouldExpand = isHeader || isExpanded(topic.id);

      let postCountForCardHeight = 0;
      if (showPosts && shouldExpand) {
        postCountForCardHeight =
          t.reorganizationTopicPosts.status === 'loaded'
            ? t.reorganizationTopicPosts.edges.length
            : t.reorganizationTopicPosts.count;
      }

      result.push({
        topic,
        key: `${topic.id}-${reorganizationMetadata.move.state}`,
        render: (style) => (
          <div
            className={styles.itemContainer}
            style={style}
            {...getPropsForStayInViewport(topic.id)}
          >
            <TopicTreeCard
              header={isHeader}
              specialColumn={specialColumn}
              tree={t}
              reorganizable={isTopicNodeReorganizable(t, currentUser)}
              ancestorsReorganizationMetadata={ancestorsReorganizationMetadata}
              selectedPosts={selectedPosts}
              expanded={shouldExpand}
              onExpandedChange={(recursive) => {
                const changes = { [topic.id]: !shouldExpand };
                if (recursive) {
                  getTopicIdFromTreeChildren(t).forEach((topicId) => {
                    changes[topicId] = !shouldExpand;
                  });
                }
                setExpandedByTopicId((prev) => ({ ...prev, ...changes }));
              }}
              postsMap={postsMap}
              loadPost={loadPost}
              {...commonProps}
            />
          </div>
        ),
        height: getCardHeight(postCountForCardHeight),
      });

      if (shouldExpand) {
        t.children.forEach((subtree) => {
          addTreeToResult(subtree, [
            ...ancestorsReorganizationMetadata,
            reorganizationMetadata,
          ]);
        });
      }
    };

    addTreeToResult(tree);

    return result;
  }, [
    isExpanded,
    getDropParams,
    isReorganizing,
    showPosts,
    tree,
    specialColumn,
    onPostSelectedChange,
    onPostDeletedChange,
    onPostPinnedChange,
    onPostArchivedChange,
    onTopicSelectedChange,
    onTopicDeletedChange,
    onTopicNameChange,
    onSubtopicCreate,
    handleDragOverChange,
    dragOverData,
    currentUser,
    selectedPosts,
    getPropsForStayInViewport,
    postsMap,
    loadPost,
  ]);

  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);

      // If the height difference in each row is quite big,
      // the total height `VariableSizeList` estimates will be very different
      // from the actual total height.
      // We fix this issue by calling `_getItemStyle`, which will force `VariableSizeList`
      // to calculate the actual height instead of giving an estimation.
      // This calculation is very fast as the `itemSize` prop is pretty lightweight.
      /* eslint-disable no-underscore-dangle */
      if (
        '_getItemStyle' in listRef.current &&
        typeof listRef.current._getItemStyle === 'function'
      ) {
        listRef.current._getItemStyle(rows.length - 1);
      }
      /* eslint-enable no-underscore-dangle */
    }
  }, [rows]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (containerRef.current) {
          setHeight(containerRef.current.getBoundingClientRect().height);
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, []);

  useLayoutEffect(() => {
    if (listInnerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (listInnerRef.current) {
          setWidth(listInnerRef.current.getBoundingClientRect().width);
        }
      });
      resizeObserver.observe(listInnerRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [height]);

  return (
    <div
      ref={dragRef}
      className={classNames({
        [styles.draggable]: isReorganizing && isRootTopicDraggable,
      })}
    >
      <div
        className={classNames(
          styles.container,
          specialColumn && styles[specialColumn],
        )}
        ref={containerRef}
      >
        {typeof height === 'number' && (
          <VariableSizeList
            onItemsRendered={({ visibleStartIndex }) => {
              const invisibleRows = rows
                .slice(0, visibleStartIndex + 1)
                .map(({ topic }) => topic)
                .filter(Boolean);

              const currentTopic = _.last(invisibleRows);
              if (currentTopic) {
                const ancestors = _.compact(
                  (currentTopic?.ancestors ?? []).map(({ id }) =>
                    invisibleRows.find((topic) => topic.id === id),
                  ),
                ).toReversed();

                setBreadcrumbs([...ancestors, currentTopic]);
              }
            }}
            ref={listRef}
            innerRef={listInnerRef}
            height={height}
            width={COLUMN_WIDTH}
            itemKey={(i) => rows[i]!.key}
            itemCount={rows.length}
            itemSize={(i) => rows[i]!.height}
            itemData={rows.map(({ render }) => render)}
            onScroll={({ scrollOffset }) => {
              const isNewHeaderStuck = scrollOffset > 0;
              if (isNewHeaderStuck !== isHeaderStuck) {
                setIsHeaderStuck(isNewHeaderStuck);
              }
            }}
          >
            {WindowItem}
          </VariableSizeList>
        )}
        {typeof width === 'number' && (
          <div
            style={{ width }}
            className={classNames(styles.header, {
              [styles.stuck]: isHeaderStuck,
            })}
          >
            <div
              className={classNames(styles.drop, {
                [styles.isDragOver]: isDragOver,
              })}
            >
              <div className={styles.headerContent}>
                <ContentMapColumnBreadcrumbs
                  movedIn={
                    tree.reorganizationMetadata.move.state ===
                    MoveStates.movedIn
                  }
                  link={!isReorganizing && !specialColumn}
                  breadcrumbs={breadcrumbs}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ContentMapColumn);
